/**
 * MarkdownStream
 *
 * The markdown rendering path for chat content, with `[n]` citation
 * superscripts wired to the citation viewer.
 *
 * The old 120ms-per-paragraph timer is gone: it faked progress on text the
 * client already had in full. Paragraphs now render immediately; on a live
 * response they fade in together via a short CSS stagger.
 *
 * Extracted verbatim from `ChatContentRenderer` so the Research dispatcher
 * (`components/Research/ResearchResponseView`) can reuse the exact same
 * renderer as the markdown fallback without importing back into it.
 */

import React, { useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Citation } from '../../store/actions/ChatAction';
import { docRefPlugin } from './utils/docRefPlugin';

/**
 * Chat markdown styling.
 *
 * Token-driven: every colour is a brand CSS var from `src/index.css`, and the
 * font is inherited from the page (`font-sans`), so the block follows the
 * rebrand - and any later font swap - without edits here.
 */
export const chatContentStyles = `
  @keyframes paragraphReveal {
    0% { opacity: 0; transform: translateY(10px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  .chat-paragraph-enter {
    /* brand draw curve - same reveal as .reveal-item */
    animation: paragraphReveal 0.34s cubic-bezier(0.45, 0, 0.3, 1) backwards;
  }

  .chat-content {
    font-family: inherit;
    font-size: 0.875rem;
    line-height: 1.6;
    color: rgb(var(--fg-body));
    margin: 0;
    padding: 0;
  }
  .chat-content p {
    margin: 0.625rem 0;
  }
  .chat-content h1, .chat-content h2, .chat-content h3, .chat-content h4, .chat-content h5, .chat-content h6 {
    font-family: inherit;
    margin: 0.875rem 0 0.375rem;
    font-weight: 600;
    color: rgb(var(--fg-strong));
  }
  .chat-content h1 { font-size: 1.2rem; }
  .chat-content h2 { font-size: 1.1rem; }
  .chat-content h3 { font-size: 1.05rem; }
  .chat-content h4 { font-size: 0.95rem; }
  .chat-content h5 { font-size: 0.875rem; }
  .chat-content h6 { font-size: 0.8125rem; }
  .chat-content ul, .chat-content ol {
    margin: 0.625rem 0;
    padding-left: 1.25rem;
    font-family: inherit;
  }
  .chat-content ul { list-style-type: disc; }
  .chat-content ol { list-style-type: decimal; }
  .chat-content li {
    margin: 0.25rem 0;
  }
  .chat-content code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    background: rgb(var(--surface-sunken));
    border: 1px solid rgb(var(--hairline-soft));
    padding: 0.1rem 0.35rem;
    border-radius: 4px;
    font-size: 0.8125rem;
    color: rgb(var(--fg-strong));
  }
  .chat-content pre {
    background: rgb(var(--surface-sunken));
    border: 1px solid rgb(var(--hairline-soft));
    padding: 0.75rem;
    border-radius: 8px;
    overflow-x: auto;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.8125rem;
    color: rgb(var(--fg-body));
    margin: 0.75rem 0;
  }
  .chat-content pre code {
    background: none;
    border: 0;
    padding: 0;
  }
  .chat-content blockquote {
    margin: 0.75rem 0;
    padding: 0.5rem 1rem;
    border-left: 2px solid rgb(var(--champagne));
    background: rgb(var(--surface-sunken) / 0.7);
    border-radius: 0 6px 6px 0;
    color: rgb(var(--fg-muted));
  }
  .chat-content sup {
    font-size: 0.7rem;
    vertical-align: super;
    margin-left: 2px;
  }
  .chat-content strong { font-weight: 600; color: rgb(var(--fg-strong)); }
  .chat-content hr {
    border: 0;
    border-top: 1px solid rgb(var(--hairline));
    margin: 1rem 0;
  }
  .chat-content table {
    border-collapse: collapse;
    margin: 1rem 0;
    width: 100%;
    font-size: 0.8125rem;
  }
  .chat-content th, .chat-content td {
    border: 1px solid rgb(var(--hairline-soft));
    padding: 0.5rem;
    text-align: left;
  }
  .chat-content th {
    background: rgb(var(--surface-sunken) / 0.7);
    font-weight: 600;
    color: rgb(var(--fg-strong));
  }
  .chat-content img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 1rem 0;
  }
  /* Draft placeholders - [BRACKETED] fields the user must still complete.
     Champagne emphasis, so an unfinished field cannot read as finished text. */
  .chat-content mark.draft-placeholder {
    background: rgb(var(--champagne) / 0.18);
    border: 1px solid rgb(var(--champagne) / 0.45);
    border-radius: 4px;
    padding: 0 0.25rem;
    color: rgb(var(--fg-strong));
    font-weight: 600;
    white-space: nowrap;
  }

  /* Citation superscripts - the same chip language as CitationChip */
  .chat-content sup a,
  .chat-content a {
    color: rgb(var(--fg-muted));
    font-weight: 600;
    text-decoration: none;
    font-size: .7em;
    vertical-align: super;
    transition: color .2s cubic-bezier(0.25, 0.7, 0.2, 1);
  }
  .chat-content sup a:hover,
  .chat-content a:hover { color: rgb(var(--champagne)); }
`;

type DocLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  ['data-doc']?: string;
};

export interface MarkdownStreamProps {
  text: string;
  citations: Citation[];
  isLive: boolean;
  onComplete: () => void;
  onOpenCitation: (url: string) => void;
}

const MarkdownStream: React.FC<MarkdownStreamProps> = ({
  text: rawText,
  citations,
  isLive,
  onComplete,
  onOpenCitation,
}) => {
  // Ensure text is always a string to prevent runtime errors on blob-loaded objects
  const text = typeof rawText === 'string' ? rawText : (rawText ? String(rawText) : '');

  // Split text into paragraph-sized chunks (double newline boundaries).
  // Preserves markdown structure within each paragraph.
  const paragraphs = useMemo(() => {
    if (!text) return [];
    // Split on double+ newlines, keeping the delimiters so markdown rendering is correct
    const parts = text.split(/(\n{2,})/);
    // Re-join into logical paragraphs (content + its trailing whitespace)
    const result: string[] = [];
    let current = '';
    for (const part of parts) {
      if (/^\n{2,}$/.test(part)) {
        if (current.trim()) result.push(current);
        current = '';
      } else {
        current += part;
      }
    }
    if (current.trim()) result.push(current);
    return result.length > 0 ? result : [text];
  }, [text]);

  // The text is already complete when it gets here - signal done immediately.
  useEffect(() => {
    onComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const citationMap = citations.reduce((acc, c) => {
    acc[c.number as number] = { url: c.url };
    return acc;
  }, {} as Record<number, { url: string }>);

  return (
    <>
      <style>{chatContentStyles}</style>
      <div className="chat-content">
        {paragraphs.map((para, i) => {
          // Live answers fade in with a short stagger; history renders flat.
          return (
            <div
              key={i}
              className={isLive ? 'chat-paragraph-enter' : ''}
              style={isLive ? { animationDelay: `${Math.min(i, 8) * 40}ms` } : undefined}
            >
              <ReactMarkdown
                remarkPlugins={[
                  remarkGfm,
                  [docRefPlugin, { map: citationMap }],
                ]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  a: ({ children, href, ...rest }) => {
                    const { ['data-doc']: dataDoc, ...anchorRest } = rest as DocLinkProps;
                    const num = Number(dataDoc ?? '');

                    return (
                      <sup>
                        <a
                          href={href}
                          data-doc={dataDoc}
                          {...anchorRest}
                          onClick={(e) => {
                            e.preventDefault();
                            const c = citations.find((c) => c.number === num);
                            if (c) onOpenCitation(c.url || `#citation-${num}`);
                          }}
                          className="text-fg-muted"
                        >
                          {children}
                        </a>
                      </sup>
                    );
                  },
                }}
              >
                {para}
              </ReactMarkdown>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default MarkdownStream;
